import { STATE_LABELS } from '../../../utility/state-colours/state-colour-registry';
/**
 * Feature flags for optional functionality.
 * Set to false to hide features that may be reinstated in the future.
 */
export const FEATURE_GLASS_BRAND_ENABLED = false;

/**
 * Display labels for certificate types.
 * Internal code uses 'AS2208' and 'AS4666', but UI should show proper standards notation.
 */
// Derived from the badge-colour registry's label entries (state-colour-standard.md section
// 8), which own the words: the type chip renders through agwa-state-badge from the same
// entries, so the chip and any text usage of these constants cannot disagree.
export const CERT_TYPE_AS2208_LABEL = STATE_LABELS.as2208.label;
export const CERT_TYPE_AS4666_LABEL = STATE_LABELS.as4666.label;

/**
 * Display labels for certificate date fields.
 * Updated for clarity during audits and data entry.
 */
export const CERTIFIED_DATE_LABEL = 'Current Certificate Issue Date';
export const CERTIFIED_DATE_TOOLTIP =
  'Date when the current certificate was issued/revalidated.';
export const EXPIRY_DATE_LABEL = 'Certificate Expiry Date';

/**
 * The labels of the issue and expiry dates that are not filled in, issue date first as the editor
 * lays them out. Both are required to leave Draft or Rejected by Submit or Approve Immediately
 * (D-140, @GA-CERT-016 and 018); an empty string counts as not set, since that is what an empty
 * date input holds.
 */
export function missingRequiredDates(
  certifiedDate: string | null | undefined,
  expiryDate: string | null | undefined
): string[] {
  const missing: string[] = [];
  if (!certifiedDate) missing.push(CERTIFIED_DATE_LABEL);
  if (!expiryDate) missing.push(EXPIRY_DATE_LABEL);
  return missing;
}

/**
 * Whether the Approve Immediately BUTTON is withheld, and the reason shown, for both detail pages:
 * by the issue and expiry dates alone (D-140, @GA-CERT-018). The Annual Review Date is no longer a
 * reason on the button (D-141): it is asked for in the confirmation the button opens, where the
 * person can set it (@GA-CERT-014). Before D-141 it withheld the button on every Draft, because no
 * Draft could ever have one, so the action could never be used. One function for both pages.
 */
export function approveImmediatelyAvailability(missingDates: string[]): {
  withheld: boolean;
  reason: string | undefined;
} {
  return {
    withheld: missingDates.length > 0,
    reason: requiredDatesReason(missingDates, 'approving immediately'),
  };
}

/**
 * Detail of `detail-dates-change`, which each certificate editor fires when its issue or expiry
 * date input changes: the two values as they now stand in the form, '' when empty.
 */
export interface CertificateDatesChangeDetail {
  certifiedDate: string;
  expiryDate: string;
}

/** The event a certificate editor fires as its issue or expiry date changes. */
export const CERTIFICATE_DATES_CHANGE_EVENT = 'detail-dates-change';

/**
 * The reason Submit or Approve Immediately is unavailable while a required date is missing, or
 * undefined when both are set: "Set the Current Certificate Issue Date and the Certificate Expiry
 * Date before submitting". Word for word the server's refusal for the same action
 * (`CertificateRequiredDates.BeforeSubmitting` / `BeforeApprovingImmediately`), so the button and
 * a refused request name the dates the same way. Mirrors the Annual Review Date tooltip's shape
 * ("Set the Annual Review Date before approving").
 */
export function requiredDatesReason(
  missing: string[],
  action: 'submitting' | 'approving immediately'
): string | undefined {
  if (missing.length === 0) return undefined;
  return `Set the ${missing.join(' and the ')} before ${action}`;
}

/**
 * Display label for product rules section (renamed to "Scope of Accreditation").
 * Internal code still uses "productRules" but UI shows user-friendly terminology.
 */
export const SCOPE_OF_ACCREDITATION_LABEL = 'Scope of Accreditation';

/**
 * Get the display label for a certificate type.
 * @param type Internal type identifier ('AS2208', 'AS4666', 'as2208', 'as4666')
 * @returns Properly formatted display label
 */
export function getCertificateTypeLabel(
  type: 'AS2208' | 'AS4666' | 'as2208' | 'as4666' | string
): string {
  const normalized = type?.toUpperCase();
  if (normalized === 'AS2208') return CERT_TYPE_AS2208_LABEL;
  if (normalized === 'AS4666') return CERT_TYPE_AS4666_LABEL;
  return type; // fallback to original
}

// ============================================================================
// Anniversary-Based Annual Review Date Utilities
// ============================================================================

/**
 * Get all anniversary dates of the expiry date between today and the expiry date.
 * Anniversaries are the same month/day as expiry in each year.
 *
 * @param expiryDate The certificate expiry date
 * @param fromDate The starting date (defaults to today)
 * @returns Array of anniversary dates in ascending order
 */
export function getExpiryAnniversaries(
  expiryDate: Date,
  fromDate: Date = new Date()
): Date[] {
  const anniversaries: Date[] = [];
  const expiry = new Date(expiryDate);
  const today = new Date(fromDate);

  // Start from the year after today, or current year if anniversary hasn't passed
  const expiryMonth = expiry.getMonth();
  const expiryDay = expiry.getDate();

  let year = today.getFullYear();

  // Check if this year's anniversary has already passed
  const thisYearAnniversary = new Date(year, expiryMonth, expiryDay);
  if (thisYearAnniversary <= today) {
    year++; // Start from next year
  }

  // Collect all anniversaries up to and including the expiry date
  while (true) {
    const anniversary = new Date(year, expiryMonth, expiryDay);

    // Stop if we've gone past the expiry date
    if (anniversary > expiry) break;

    // Only include future dates
    if (anniversary > today) {
      anniversaries.push(anniversary);
    }

    year++;
  }

  return anniversaries;
}

/**
 * Calculate the initial annual review date when a certificate is approved.
 *
 * Logic:
 * 1. Find the next anniversary of the expiry date from today
 * 2. If that anniversary is less than 6 months away, use the following anniversary
 * 3. Exception: If the next anniversary IS the expiry date, use it (no more reviews needed)
 *
 * @param expiryDate The certificate expiry date
 * @param fromDate The approval date (defaults to today)
 * @returns The calculated initial review date, or null if no review needed
 */
export function calculateInitialReviewDate(
  expiryDate: Date,
  fromDate: Date = new Date()
): Date | null {
  const anniversaries = getExpiryAnniversaries(expiryDate, fromDate);

  if (anniversaries.length === 0) {
    return null; // No anniversaries before expiry - no review needed
  }

  const today = new Date(fromDate);
  const expiry = new Date(expiryDate);
  const sixMonthsFromNow = new Date(today);
  sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);

  const nextAnniversary = anniversaries[0];

  // If the next anniversary is the expiry date itself, use it (final review)
  if (isSameDay(nextAnniversary, expiry)) {
    return nextAnniversary;
  }

  // If next anniversary is less than 6 months away, use the following one
  if (nextAnniversary < sixMonthsFromNow) {
    // Check if there's a following anniversary
    if (anniversaries.length > 1) {
      return anniversaries[1];
    }
    // If no following anniversary, the next one is close to expiry - use it
    return nextAnniversary;
  }

  return nextAnniversary;
}

/**
 * Get available anniversary options for changing the annual review date.
 * Used in the manual review UI to offer Previous/Next anniversary buttons.
 *
 * @param currentReviewDate The current annual review date
 * @param expiryDate The certificate expiry date
 * @returns Object with optional previous and next anniversary dates
 */
export function getReviewDateOptions(
  currentReviewDate: Date,
  expiryDate: Date
): { previous?: Date; next?: Date } {
  const result: { previous?: Date; next?: Date } = {};
  const today = new Date();
  const expiry = new Date(expiryDate);
  const current = new Date(currentReviewDate);

  const expiryMonth = expiry.getMonth();
  const expiryDay = expiry.getDate();

  // Calculate previous anniversary (same month/day, previous year)
  const previousYear = current.getFullYear() - 1;
  const previousAnniversary = new Date(previousYear, expiryMonth, expiryDay);

  // Previous is valid if it's still in the future
  if (previousAnniversary > today) {
    result.previous = previousAnniversary;
  }

  // Calculate next anniversary (same month/day, next year)
  const nextYear = current.getFullYear() + 1;
  const nextAnniversary = new Date(nextYear, expiryMonth, expiryDay);

  // Next is valid if it's on or before the expiry date
  if (nextAnniversary <= expiry) {
    result.next = nextAnniversary;
  }

  return result;
}

/**
 * Check if two dates are the same day (ignoring time).
 */
function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

/**
 * Format a date as a localized string for display.
 */
export function formatReviewDate(date: Date | string | undefined): string {
  if (!date) return 'Not set';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return 'Invalid date';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format a date as ISO string for form values (YYYY-MM-DD).
 */
export function toISODateString(date: Date): string {
  return date.toISOString().split('T')[0];
}

/**
 * The Annual Review Date offered for a certificate that has none yet, as a date input wants it
 * (YYYY-MM-DD), or '' when there is no expiry to derive it from or no anniversary before it.
 *
 * The same derivation an auditor locking a certificate for review is offered when it has no review
 * date (the page's handleLockForReview), so Approve Immediately offers the creator-auditor exactly
 * what the two-person path would (D-141, @GA-CERT-014).
 */
export function initialReviewDateFor(
  expiryDate: string | null | undefined
): string {
  if (!expiryDate) return '';
  const calculated = calculateInitialReviewDate(new Date(expiryDate));
  return calculated ? toISODateString(calculated) : '';
}
